<script setup lang="ts">
    import { reactive, computed, watch } from 'vue';
    import SingleSelect from '@shared/components/SingleSelect.vue';
    import MultiSelect from '@shared/components/MultiSelect/MultiSelect.vue';
    import Loading from '@shared/components/Loading.vue';
    import { getQuestionGroupedBy, buildQueryString, type DateRange } from '../SurveyStatistics.access';
    import { QuestionType, type Question, type QuestionFilter, type QuestionGroupedByResult } from '../../SurveysTypes.d';

    const props = defineProps<{
        surveyId: string;
        question: Question;
        questions: Question[];
        dateRange: DateRange;
        questionFilters: QuestionFilter[];
        selectedCultures: string[];
    }>();

    const emit = defineEmits<{
        (e: 'update:questionOptionFilters', question: Question, selectedOptionIds: string[]): void;
    }>();

    const nonGroupableTypes = [QuestionType.Content, QuestionType.Pagebreak, QuestionType.Divider];

    const groupableQuestions = computed(
        () =>
            props.questions.filter((q) => q.QuestionId !== props.question.QuestionId && !nonGroupableTypes.includes(q.Type)) as unknown as Record<
                string,
                unknown
            >[]
    );

    const filterableQuestionOptions = computed(() => props.question.QuestionOptions?.filter((o) => o.ResponseCount > 0) ?? []);

    const selectedQuestionOptionIds = computed(() =>
        props.questionFilters.filter((f) => f.QuestionId === props.question.QuestionId && f.QuestionOptionId).map((f) => f.QuestionOptionId!)
    );

    function updateQuestionOptionFilters(selectedOptionIds: string[]) {
        emit('update:questionOptionFilters', props.question, selectedOptionIds);
    }

    const state = reactive<{
        groupByQuestionId: string;
        results: QuestionGroupedByResult[];
        loading: boolean;
    }>({
        groupByQuestionId: '',
        results: [],
        loading: false,
    });

    async function onGroupByChange() {
        if (!state.groupByQuestionId) {
            state.results = [];
            return;
        }

        state.loading = true;
        state.results = [];
        try {
            const qs = buildQueryString(props.dateRange, props.questionFilters, props.selectedCultures);
            state.results = await getQuestionGroupedBy(props.surveyId, props.question.QuestionId, state.groupByQuestionId, qs);
        } finally {
            state.loading = false;
        }
    }

    watch(() => state.groupByQuestionId, onGroupByChange);
</script>

<template>
    <template v-if="filterableQuestionOptions.length > 0 || question.AverageNumericValue > 0">
        <div class="row">
            <div class="col-md-6">
                <MultiSelect
                    v-if="filterableQuestionOptions.length > 0"
                    class="form-group m-sm-b"
                    :label="$Resources.SurveyQuestionOptionFilterLabel"
                    :items="filterableQuestionOptions"
                    selectProp="QuestionOptionId"
                    selectOnly="QuestionOptionId"
                    displayProp="Value"
                    :selectedItems="selectedQuestionOptionIds"
                    @update:selectedItems="updateQuestionOptionFilters" />
            </div>
            <div class="col-md-6" v-if="question.AverageNumericValue > 0">
                <SingleSelect
                    class="form-group m-sm-b"
                    v-model="state.groupByQuestionId"
                    :items="groupableQuestions"
                    selectProp="QuestionId"
                    displayProps="Title"
                    :label="$Resources.GroupByLabel"
                    :infoText="$Resources.GroupByInfoText" />
            </div>
        </div>

        <div v-if="state.groupByQuestionId" class="custom-scrollbar mx-h-400 m-sm-b">
            <div class="table-responsive">
                <table class="table table-hover align-middle m-0-b">
                    <thead>
                        <tr>
                            <th>{{ $Resources.GroupHeading }}</th>
                            <th class="text-right text-nowrap">{{ $Resources.AverageLabel }}</th>
                            <th class="text-right text-nowrap">{{ $Resources.SurveyResponseCountLabel }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="group in state.results" :key="group.Group">
                            <td>{{ group.Group }}</td>
                            <td class="text-right text-nowrap strong">{{ group.AverageNumericValue?.toFixed(2) ?? '—' }}</td>
                            <td class="text-right text-nowrap">{{ group.ResponseCount }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </template>

    <Loading :show="state.loading" loadingStyle="progress-bar" fixed />
</template>

